import { useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  type CreateLabKitExpirationRequest,
  type CreateLabKitRequest,
  type CreateTrialLaboratoryRequest,
} from '@ctms/contracts';
import {
  ChevronDown,
  ChevronRight,
  ExternalLink,
  FlaskConical,
  Mail,
  MapPin,
  Package,
  Plus,
  Sparkles,
  X,
} from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { kitsApi } from '../kits.api.js';
import { kitQueryKeys } from '../kits.query-keys.js';

export interface TrialKitsCatalogProps {
  trialId: number;
  canWrite: boolean;
}

export function TrialKitsCatalog({ trialId, canWrite }: TrialKitsCatalogProps): JSX.Element {
  const queryClient = useQueryClient();
  const [selectedLabId, setSelectedLabId] = useState<number | null>(null);
  const [expandedKitId, setExpandedKitId] = useState<number | null>(null);

  // Dialog states
  const [isAddLabOpen, setIsAddLabOpen] = useState(false);
  const [isAddKitOpen, setIsAddKitOpen] = useState(false);
  const [isAddBatchOpen, setIsAddBatchOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Form states for new lab
  const [newLabName, setNewLabName] = useState('');
  const [newLabNotes, setNewLabNotes] = useState('');
  const [newLabEmail, setNewLabEmail] = useState('');
  const [newLabWww, setNewLabWww] = useState('');
  const [newLabResupplyDesc, setNewLabResupplyDesc] = useState('');

  // Form states for new kit
  const [newKitName, setNewKitName] = useState('');
  const [newKitDescription, setNewKitDescription] = useState('');
  const [newKitNotes, setNewKitNotes] = useState('');
  const [newKitLocalisation, setNewKitLocalisation] = useState('');
  const [newKitExpiration, setNewKitExpiration] = useState(true);

  // Form states for new batch
  const [newBatchDate, setNewBatchDate] = useState('');
  const [newBatchComment, setNewBatchComment] = useState('');

  // Query: Laboratories for trial (REQ-KIT-001)
  const laboratoriesQuery = useQuery({
    queryKey: kitQueryKeys.laboratories.byTrial(trialId),
    queryFn: () => kitsApi.listLaboratories({ trialId, page: 1, limit: 100 }),
    enabled: trialId > 0,
    staleTime: 5 * 60 * 1000,
  });

  const laboratories = laboratoriesQuery.data?.items ?? [];
  const activeLabId = selectedLabId ?? (laboratories.length > 0 ? laboratories[0]?.id ?? null : null);
  const activeLab = laboratories.find((lab) => lab.id === activeLabId);

  // Query: Kits for active laboratory (REQ-KIT-003)
  const kitsQuery = useQuery({
    queryKey: kitQueryKeys.kits.byLab(activeLabId ?? 0),
    queryFn: () => kitsApi.listKits({ trialLaboratoryId: activeLabId ?? 0, page: 1, limit: 100 }),
    enabled: Boolean(activeLabId),
    staleTime: 5 * 60 * 1000,
  });

  const kits = kitsQuery.data?.items ?? [];

  // Query: Expirations for expanded kit (REQ-KIT-005)
  const expirationsQuery = useQuery({
    queryKey: kitQueryKeys.kits.expirations(expandedKitId ?? 0),
    queryFn: () => kitsApi.listExpirations(expandedKitId ?? 0),
    enabled: Boolean(expandedKitId),
    staleTime: 5 * 60 * 1000,
  });

  const expirations = expirationsQuery.data ?? [];

  // Mutation: Add laboratory (REQ-KIT-001)
  const addLabMutation = useMutation({
    mutationFn: (request: CreateTrialLaboratoryRequest) => kitsApi.createLaboratory(request),
    onSuccess: (created) => {
      setIsAddLabOpen(false);
      setNewLabName('');
      setNewLabNotes('');
      setNewLabEmail('');
      setNewLabWww('');
      setNewLabResupplyDesc('');
      setSelectedLabId(created.id);
      void queryClient.invalidateQueries({ queryKey: kitQueryKeys.laboratories.byTrial(trialId) });
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się dodać laboratorium.').then(setErrorMessage);
    },
  });

  // Mutation: Add kit (REQ-KIT-003, REQ-KIT-004)
  const addKitMutation = useMutation({
    mutationFn: (request: CreateLabKitRequest) => kitsApi.createKit(request),
    onSuccess: () => {
      setIsAddKitOpen(false);
      setNewKitName('');
      setNewKitDescription('');
      setNewKitNotes('');
      setNewKitLocalisation('');
      setNewKitExpiration(true);
      void queryClient.invalidateQueries({ queryKey: kitQueryKeys.kits.byLab(activeLabId ?? 0) });
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się dodać kitu.').then(setErrorMessage);
    },
  });

  // Mutation: Add batch expiration (REQ-KIT-005)
  const addBatchMutation = useMutation({
    mutationFn: (request: { kitId: number; payload: CreateLabKitExpirationRequest }) =>
      kitsApi.createExpiration(request.kitId, request.payload),
    onSuccess: () => {
      setIsAddBatchOpen(false);
      setNewBatchDate('');
      setNewBatchComment('');
      if (expandedKitId) {
        void queryClient.invalidateQueries({
          queryKey: kitQueryKeys.kits.expirations(expandedKitId),
        });
      }
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się dodać partii daty ważności.').then(setErrorMessage);
    },
  });

  const handleCreateLab = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!newLabName.trim()) return;
    setErrorMessage(null);
    addLabMutation.mutate({
      trialId,
      name: newLabName.trim(),
      notes: newLabNotes.trim() || undefined,
      resupplyLocal: true,
      resupplyDescription: newLabResupplyDesc.trim() || undefined,
      resupplyEmail: newLabEmail.trim() || undefined,
      resupplyWww: newLabWww.trim() || undefined,
    });
  };

  const handleCreateKit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!activeLabId || !newKitName.trim()) return;
    setErrorMessage(null);
    addKitMutation.mutate({
      trialLaboratoryId: activeLabId,
      name: newKitName.trim(),
      description: newKitDescription.trim() || undefined,
      notes: newKitNotes.trim() || undefined,
      localisation: newKitLocalisation.trim() || undefined,
      expiration: newKitExpiration,
    });
  };

  const handleCreateBatch = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!expandedKitId || !newBatchDate.trim()) return;
    setErrorMessage(null);
    addBatchMutation.mutate({
      kitId: expandedKitId,
      payload: {
        expirationDate: newBatchDate.trim(),
        batchComment: newBatchComment.trim() || undefined,
      },
    });
  };

  return (
    <section
      aria-label="Katalog kitów i laboratoriów badania"
      className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 text-slate-100"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-800 pb-3">
        <div className="flex items-center gap-2.5">
          <div className="flex h-7 w-7 items-center justify-center rounded-md bg-cyan-950 text-cyan-400 ring-1 ring-cyan-800">
            <Package className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-slate-100">
              Laboratoria i Kity Badania (Domena 5)
            </h3>
            <p className="text-[11px] text-slate-400">
              Zdefiniowane laboratoria, katalog kitów i partie z zasadą FEFO
            </p>
          </div>
        </div>

        {canWrite && (
          <button
            type="button"
            data-testid="add-laboratory-button"
            onClick={() => setIsAddLabOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md bg-cyan-700/80 px-2.5 py-1 text-xs font-medium text-white hover:bg-cyan-600 transition-colors"
          >
            <Plus className="h-3.5 w-3.5" />
            <span>Dodaj laboratorium</span>
          </button>
        )}
      </div>

      {/* Main Grid: Laboratories list + Kits list */}
      <div className="mt-4 grid grid-cols-3 gap-4">
        {/* Left: Laboratories column */}
        <div className="rounded-md border border-slate-800/80 bg-slate-950/50 p-3">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-400">
            Laboratoria ({laboratories.length})
          </div>

          {laboratories.length === 0 ? (
            <div className="py-8 text-center text-xs text-slate-500">
              <FlaskConical className="mx-auto h-6 w-6 text-slate-600 mb-1" />
              <span>Brak laboratoriów w tym badaniu.</span>
            </div>
          ) : (
            <div className="space-y-1.5">
              {laboratories.map((lab) => (
                <button
                  key={lab.id}
                  type="button"
                  data-testid={`lab-tab-${lab.id}`}
                  onClick={() => setSelectedLabId(lab.id)}
                  className={`w-full rounded-md p-2.5 text-left text-xs transition-colors ${
                    lab.id === activeLabId
                      ? 'border border-cyan-800 bg-cyan-950/40 text-cyan-200'
                      : 'border border-transparent hover:bg-slate-900 text-slate-300'
                  }`}
                >
                  <div className="font-semibold">{lab.name}</div>
                  {lab.notes && (
                    <div className="text-[11px] text-slate-400 truncate">{lab.notes}</div>
                  )}
                  {lab.resupplyEmail && (
                    <div className="mt-1 flex items-center gap-1 text-[10px] text-slate-400">
                      <Mail className="h-3 w-3 text-slate-500" />
                      <span>{lab.resupplyEmail}</span>
                    </div>
                  )}
                </button>
              ))}
            </div>
          )}

          {activeLab && (
            <div className="mt-4 border-t border-slate-800/80 pt-3 text-[11px] text-slate-400 space-y-1">
              <div className="font-semibold text-slate-300">Zamawianie kitów (REQ-KIT-002):</div>
              {activeLab.resupplyDescription && <div>{activeLab.resupplyDescription}</div>}
              {activeLab.resupplyWww && (
                <a
                  href={activeLab.resupplyWww}
                  target="_blank"
                  rel="noreferrer"
                  className="flex items-center gap-1 text-cyan-400 underline hover:text-cyan-300"
                >
                  <ExternalLink className="h-3 w-3" />
                  <span>{activeLab.resupplyWww}</span>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Right: Kits list for active laboratory (takes 2 columns) */}
        <div className="col-span-2 rounded-md border border-slate-800/80 bg-slate-950/50 p-3">
          <div className="flex items-center justify-between border-b border-slate-800/60 pb-2">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">
                Katalog Kitów {activeLab ? `— ${activeLab.name}` : ''}
              </div>
              <span className="text-[11px] text-slate-400">
                {kits.length} zdefiniowanych typów kitów
              </span>
            </div>

            {canWrite && activeLab && (
              <button
                type="button"
                data-testid="add-kit-button"
                onClick={() => setIsAddKitOpen(true)}
                className="inline-flex items-center gap-1 rounded border border-cyan-800 bg-cyan-950/50 px-2 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>Dodaj kit</span>
              </button>
            )}
          </div>

          {!activeLab ? (
            <div className="py-12 text-center text-xs text-slate-500">
              Wybierz lub dodaj laboratorium, aby zobaczyć kity.
            </div>
          ) : kits.length === 0 ? (
            <div className="py-12 text-center text-xs text-slate-500">
              <Package className="mx-auto h-7 w-7 text-slate-700 mb-2" />
              <span>Brak zdefiniowanych kitów w tym laboratorium.</span>
            </div>
          ) : (
            <div className="mt-3 divide-y divide-slate-800/70">
              {kits.map((kit) => {
                const isExpanded = expandedKitId === kit.id;

                return (
                  <div key={kit.id} className="py-2.5">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {kit.expiration ? (
                          <button
                            type="button"
                            onClick={() => setExpandedKitId(isExpanded ? null : kit.id)}
                            className="text-slate-400 hover:text-slate-200"
                            title={isExpanded ? 'Zwiń partie' : 'Rozwiń partie'}
                          >
                            {isExpanded ? (
                              <ChevronDown className="h-4 w-4" />
                            ) : (
                              <ChevronRight className="h-4 w-4" />
                            )}
                          </button>
                        ) : (
                          <div className="w-4" />
                        )}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-slate-200">{kit.name}</span>
                            {kit.expiration ? (
                              <span className="rounded bg-cyan-950/80 px-1.5 py-0.2 text-[10px] text-cyan-300 border border-cyan-800/60">
                                z datą ważności (FEFO)
                              </span>
                            ) : (
                              <span className="rounded bg-slate-800 px-1.5 py-0.2 text-[10px] text-slate-400">
                                bezterminowy
                              </span>
                            )}
                          </div>
                          {kit.description && (
                            <p className="text-[11px] text-slate-400">{kit.description}</p>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-right">
                        {kit.localisation && (
                          <div className="flex items-center gap-1 text-[11px] text-slate-400">
                            <MapPin className="h-3 w-3 text-slate-500" />
                            <span>{kit.localisation}</span>
                          </div>
                        )}

                        {kit.expiration && canWrite && (
                          <button
                            type="button"
                            data-testid={`add-batch-button-${kit.id}`}
                            onClick={() => {
                              setExpandedKitId(kit.id);
                              setIsAddBatchOpen(true);
                            }}
                            className="rounded border border-slate-700 px-2 py-0.5 text-[11px] text-slate-300 hover:bg-slate-800"
                          >
                            + Partia
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Expiration batches list when expanded (REQ-KIT-005, REQ-KIT-006) */}
                    {isExpanded && kit.expiration && (
                      <div className="ml-6 mt-2.5 rounded-md border border-slate-800 bg-slate-900/90 p-3">
                        <div className="flex items-center justify-between text-[11px] text-slate-400 mb-2">
                          <span className="font-semibold text-slate-300">
                            Partie daty ważności (kolejność FEFO):
                          </span>
                          <span className="text-cyan-400 text-[10px]">
                            Najwcześniejsza data sugerowana w pierwszej kolejności
                          </span>
                        </div>

                        {expirations.length === 0 ? (
                          <div className="py-2 text-[11px] text-slate-500 italic">
                            Brak wprowadzonych partii z datami ważności.
                          </div>
                        ) : (
                          <div className="space-y-1">
                            {expirations.map((batch, batchIdx) => (
                              <div
                                key={batch.id}
                                className="flex items-center justify-between rounded bg-slate-950/60 px-2 py-1 text-xs"
                              >
                                <div className="flex items-center gap-2">
                                  <span className="font-mono text-slate-200">
                                    {batch.expirationDate}
                                  </span>
                                  {batchIdx === 0 && (
                                    <span className="inline-flex items-center gap-1 rounded bg-emerald-950 px-1.5 py-0.2 text-[10px] text-emerald-300 ring-1 ring-emerald-800">
                                      <Sparkles className="h-2.5 w-2.5" />
                                      FEFO
                                    </span>
                                  )}
                                </div>
                                <span className="text-[11px] text-slate-400">
                                  {batch.batchComment ?? '—'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* DIALOG: Dodaj laboratorium (REQ-KIT-001) */}
      {isAddLabOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dodaj laboratorium"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-semibold text-slate-100">
                Dodaj laboratorium do badania
              </h3>
              <button
                type="button"
                onClick={() => setIsAddLabOpen(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateLab} className="mt-4 space-y-3 text-xs">
              {errorMessage && (
                <p role="alert" className="text-xs text-rose-400">
                  {errorMessage}
                </p>
              )}
              <div>
                <label htmlFor="input-lab-name" className="block font-medium text-slate-300 mb-1">
                  Nazwa laboratorium *
                </label>
                <input
                  id="input-lab-name"
                  type="text"
                  required
                  data-testid="input-lab-name"
                  value={newLabName}
                  onChange={(e) => setNewLabName(e.target.value)}
                  placeholder="np. Central BioLab Warszawa"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Uwagi / Wytyczne</label>
                <input
                  type="text"
                  value={newLabNotes}
                  onChange={(e) => setNewLabNotes(e.target.value)}
                  placeholder="np. Krew mrożona -20°C"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">
                  E-mail do zamówień kitów
                </label>
                <input
                  type="email"
                  value={newLabEmail}
                  onChange={(e) => setNewLabEmail(e.target.value)}
                  placeholder="zamowienia@laboratorium.pl"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">
                  Strona WWW do zamówień
                </label>
                <input
                  type="url"
                  value={newLabWww}
                  onChange={(e) => setNewLabWww(e.target.value)}
                  placeholder="https://portal.laboratorium.pl"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-medium text-slate-300 mb-1">Opis zamawiania</label>
                <textarea
                  value={newLabResupplyDesc}
                  onChange={(e) => setNewLabResupplyDesc(e.target.value)}
                  placeholder="np. Zamawiać z 7-dniowym wyprzedzeniem formularzem..."
                  rows={2}
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddLabOpen(false)}
                  className="rounded border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addLabMutation.isPending}
                  className="rounded bg-cyan-600 px-3.5 py-1.5 font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  {addLabMutation.isPending ? 'Zapisywanie…' : 'Zapisz laboratorium'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DIALOG: Dodaj kit do laboratorium (REQ-KIT-003, REQ-KIT-004) */}
      {isAddKitOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dodaj kit do laboratorium"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
        >
          <div className="w-full max-w-md rounded-lg border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-semibold text-slate-100">
                Dodaj kit ({activeLab?.name})
              </h3>
              <button
                type="button"
                onClick={() => setIsAddKitOpen(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateKit} className="mt-4 space-y-3 text-xs">
              {errorMessage && (
                <p role="alert" className="text-xs text-rose-400">
                  {errorMessage}
                </p>
              )}

              <div>
                <label htmlFor="input-kit-name" className="block font-medium text-slate-300 mb-1">
                  Nazwa kitu (z opakowania) *
                </label>
                <input
                  id="input-kit-name"
                  type="text"
                  required
                  data-testid="input-kit-name"
                  value={newKitName}
                  onChange={(e) => setNewKitName(e.target.value)}
                  placeholder="np. Screening blood kit"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="input-kit-localisation" className="block font-medium text-slate-300 mb-1">
                  Lokalizacja przechowywania
                </label>
                <input
                  id="input-kit-localisation"
                  type="text"
                  value={newKitLocalisation}
                  onChange={(e) => setNewKitLocalisation(e.target.value)}
                  placeholder="np. Szafa B, półka 3 / Lodówka 4°C"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="input-kit-description" className="block font-medium text-slate-300 mb-1">Opis kitu</label>
                <input
                  id="input-kit-description"
                  type="text"
                  value={newKitDescription}
                  onChange={(e) => setNewKitDescription(e.target.value)}
                  placeholder="np. Zestaw z probówkami EDTA i strzykawkami"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              {/* Expiration split: mandatory choice (REQ-KIT-004) */}
              <div className="border border-slate-800 rounded p-2.5 bg-slate-950/40">
                <span className="block font-medium text-slate-300 mb-2">
                  Klasyfikacja ważności (REQ-KIT-004) *
                </span>
                <div className="space-y-1.5">
                  <label className="flex items-center gap-2 text-slate-200 cursor-pointer">
                    <input
                      type="radio"
                      name="kit-expiration-type"
                      checked={newKitExpiration}
                      onChange={() => setNewKitExpiration(true)}
                      className="accent-cyan-500"
                    />
                    <span>Z datą przydatności (śledzone partiami, reguła FEFO)</span>
                  </label>
                  <label className="flex items-center gap-2 text-slate-200 cursor-pointer">
                    <input
                      type="radio"
                      name="kit-expiration-type"
                      checked={!newKitExpiration}
                      onChange={() => setNewKitExpiration(false)}
                      className="accent-cyan-500"
                    />
                    <span>Bezterminowy (np. kubeczki, pipety)</span>
                  </label>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddKitOpen(false)}
                  className="rounded border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addKitMutation.isPending}
                  className="rounded bg-cyan-600 px-3.5 py-1.5 font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  {addKitMutation.isPending ? 'Zapisywanie…' : 'Zapisz kit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* DIALOG: Dodaj partię daty ważności (REQ-KIT-005) */}
      {isAddBatchOpen && (
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Dodaj partię z datą ważności"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 p-4"
        >
          <div className="w-full max-w-sm rounded-lg border border-slate-800 bg-slate-900 p-6 text-slate-100 shadow-xl">
            <div className="flex items-center justify-between border-b border-slate-800 pb-3">
              <h3 className="text-base font-semibold text-slate-100">
                Nowa partia daty ważności
              </h3>
              <button
                type="button"
                onClick={() => setIsAddBatchOpen(false)}
                className="text-slate-400 hover:text-slate-200"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={handleCreateBatch} className="mt-4 space-y-3 text-xs">
              {errorMessage && (
                <p role="alert" className="text-xs text-rose-400">
                  {errorMessage}
                </p>
              )}

              <div>
                <label htmlFor="input-batch-date" className="block font-medium text-slate-300 mb-1">
                  Data ważności (RRRR-MM-DD lub RRRR-MM) *
                </label>
                <input
                  id="input-batch-date"
                  type="text"
                  required
                  data-testid="input-batch-date"
                  value={newBatchDate}
                  onChange={(e) => setNewBatchDate(e.target.value)}
                  placeholder="2027-06-30"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 font-mono text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div>
                <label htmlFor="input-batch-comment" className="block font-medium text-slate-300 mb-1">
                  Komentarz do partii
                </label>
                <input
                  id="input-batch-comment"
                  type="text"
                  data-testid="input-batch-comment"
                  value={newBatchComment}
                  onChange={(e) => setNewBatchComment(e.target.value)}
                  placeholder="np. Partia #LOT-892"
                  className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-1.5 text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => setIsAddBatchOpen(false)}
                  className="rounded border border-slate-700 px-3 py-1.5 text-slate-300 hover:bg-slate-800"
                >
                  Anuluj
                </button>
                <button
                  type="submit"
                  disabled={addBatchMutation.isPending}
                  className="rounded bg-cyan-600 px-3.5 py-1.5 font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  {addBatchMutation.isPending ? 'Zapisywanie…' : 'Zapisz partię'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
